import {api} from '@/shared/services/api-client';
import {queryKeys} from '@/lib/query-keys';
import type {NoShowLimitEntry} from '@/shared/lib/schedule-moderators';

/**
 * PM allow/block decisions on (participant, session type) pairs that hit the
 * no-show/cancellation limit (OD-1102, confirmation dialogs added in
 * OD-1130). This is the single source of truth for fetching/saving/
 * interpreting these decisions — Action Center (the only place decisions are
 * made or unblocked), the Scheduler grid, and the PM booking page (both of
 * which only need to know what's blocked) all read through this module so
 * they share one cache entry instead of each independently hitting
 * `GET /booking-decisions`.
 */
export interface BookingDecision {
  participant_id: string;
  session_type_id: string;
  decision: 'allowed' | 'blocked';
}

export function bookingDecisionsQueryKey(projectId: string) {
  return queryKeys.actionCenter.bookingDecisions(projectId);
}

const decisionKey = (participantId: string, sessionTypeId: string) =>
  `${participantId}|${sessionTypeId}`;

/** Map of "participantId|sessionTypeId" -> decision, for O(1) lookup per entry. */
export function buildBookingDecisionMap(
  decisions: BookingDecision[],
): Map<string, 'allowed' | 'blocked'> {
  const map = new Map<string, 'allowed' | 'blocked'>();
  for (const d of decisions) {
    map.set(decisionKey(d.participant_id, d.session_type_id), d.decision);
  }
  return map;
}

/** Set of "participantId|sessionTypeId" currently blocked — the only shape the Scheduler grid and PM booking page need. */
export function buildBlockedBookingPairKeys(decisions: BookingDecision[]): Set<string> {
  const blocked = new Set<string>();
  for (const d of decisions) {
    if (d.decision === 'blocked') blocked.add(decisionKey(d.participant_id, d.session_type_id));
  }
  return blocked;
}

/**
 * No-show/cancellation-limit entries still worth showing a PM (OD-1102):
 * "allowed" is a permanent dismissal — no re-alert — so those pairs are
 * filtered out here; "blocked" pairs stay (Action Center's card keeps them
 * visible with an Unblock action) and undecided pairs obviously stay too.
 * Shared by the Action Center card itself and the sidebar nav badge so the
 * two numbers can't drift apart.
 */
export function filterVisibleNoShowLimitEntries(
  entries: NoShowLimitEntry[],
  decisions: BookingDecision[],
): NoShowLimitEntry[] {
  const decisionByKey = buildBookingDecisionMap(decisions);
  return entries.filter(
    (e) => decisionByKey.get(decisionKey(e.participantId, e.sessionTypeId)) !== 'allowed',
  );
}

export async function fetchBookingDecisionsRaw(projectId: string): Promise<BookingDecision[]> {
  const res = await api.bookingDecisions.list(projectId);
  const decisions = res.data?.data ?? res.data ?? [];
  return Array.isArray(decisions) ? decisions : [];
}

export async function saveBookingDecisionRaw(input: {
  participantId: string;
  projectId: string;
  sessionTypeId: string;
  decision: 'allowed' | 'blocked';
}): Promise<BookingDecision> {
  const res = await api.bookingDecisions.upsert({
    participant_id: input.participantId,
    project_id: input.projectId,
    session_type_id: input.sessionTypeId,
    decision: input.decision,
  });
  return res.data?.data ?? res.data;
}
